import { type Store, deriveStore } from '@frontend/viewer-core';

import { deriveModel } from './deriveModel';
import type { HistoryStore } from './historyStore';
import type { PointerTracker } from './pointerTracker';
import { isBrushTool } from './toolController';
import type {
  DrawingModel,
  DxfSceneLoadInput,
  DxfSceneLoadState,
  DxfSessionStatus,
  LayerManager,
  MappingManager,
  MarqueeController,
  MeasureManager,
  ModeManager,
  SelectionGranularityManager,
  SelectionManager,
  ToolManager,
} from './types';
import type { ViewportController } from './viewportController';

type Dependencies = {
  load: Store<DxfSceneLoadState>;
  viewport: Pick<ViewportController, 'reset'>;
  layers: Pick<LayerManager, 'setAllVisible' | 'setHovered'>;
  selection: Pick<SelectionManager, 'clear'>;
  selectionGranularity: Pick<SelectionGranularityManager, 'set'>;
  measure: Pick<MeasureManager, 'clear'>;
  marqueeController: Pick<MarqueeController, 'abort'>;
  pointerTracker: Pick<PointerTracker, 'reset'>;
  mode: Pick<ModeManager, 'set'>;
  mapping: Pick<MappingManager, 'endSession' | 'loadSaved'>;
  history: Pick<HistoryStore, 'reset'>;
  tools: Pick<ToolManager, 'set' | 'getState'>;
};

export type SceneLoader = (input: DxfSceneLoadInput) => void;

const sceneUrlOf = (input: DxfSceneLoadInput): string | null =>
  input.status === 'idle' ? null : input.sceneUrl;

const isSameLoad = (
  current: DxfSceneLoadState,
  next: DxfSceneLoadState,
): boolean => {
  if (current.status !== next.status) {
    return false;
  }
  switch (next.status) {
    case 'idle':
      return true;
    case 'loading':
      return current.status === 'loading' && current.sceneUrl === next.sceneUrl;
    case 'error':
      return (
        current.status === 'error' &&
        current.sceneUrl === next.sceneUrl &&
        current.error === next.error
      );
    case 'ready':
      return (
        current.status === 'ready' &&
        current.sceneUrl === next.sceneUrl &&
        current.model === next.model
      );
    default: {
      const _exhaustive: never = next;
      return _exhaustive;
    }
  }
};

/**
 * Narrow read of `load.status` for callers that only need the discriminant.
 */
export const createStatusStore = (
  load: Store<DxfSceneLoadState>,
): ReturnType<typeof deriveStore<DxfSceneLoadState, DxfSessionStatus>> =>
  deriveStore(load, (current) => current.status);

/**
 * Narrow read of `load.model` for imperative controllers and the renderer.
 */
export const createModelStore = (
  load: Store<DxfSceneLoadState>,
): ReturnType<typeof deriveStore<DxfSceneLoadState, DrawingModel | null>> =>
  deriveStore(load, (current) => current.model);

/**
 * Replaces the drawing and re-frames it, on demand from `DxfViewerSession.loadScene`.
 *
 * Resets against `sceneUrl` for a different file, and against a re-derived
 * model when the same URL delivers a new `scene` object — the object is a fresh
 * reference every time its query refetches even when the backend returns
 * byte-identical content, and comparing objects alone would either thrash ids on
 * every parent re-render or, worse, keep loop-id selection across a derivation
 * that no longer matches. Identical scene identity reuses the model; a new
 * object re-derives and resets session state.
 *
 * The reset itself is composed entirely from other controllers' own public
 * methods (`layers.setAllVisible`, `selection.clear`, `viewport.reset`, …)
 * rather than reaching into shared state directly — each of those already
 * knows how to clear its own slice correctly (`selection.clear` also drops
 * the drill state it owns internally, `viewport.reset` already zeroes
 * rotation and re-fits), so this function only has to decide *when*, not *how*.
 */
export const createSceneLoader = ({
  load,
  viewport,
  layers,
  selection,
  selectionGranularity,
  measure,
  marqueeController,
  pointerTracker,
  mode,
  mapping,
  history,
  tools,
}: Dependencies): SceneLoader => {
  /** The URL last seen, or `undefined` before the first call. */
  let previousSceneUrl: string | null | undefined;

  return (input) => {
    const previous = load.getState();
    const previousModel = previous.model;
    const sceneUrl = sceneUrlOf(input);
    const isNewDrawing = sceneUrl !== previousSceneUrl;
    previousSceneUrl = sceneUrl;

    let next: DxfSceneLoadState;
    switch (input.status) {
      case 'idle':
        next = { status: 'idle', model: null };
        break;
      case 'loading':
        next = { status: 'loading', sceneUrl: input.sceneUrl, model: null };
        break;
      case 'error':
        next = {
          status: 'error',
          sceneUrl: input.sceneUrl,
          model: null,
          error: input.error,
        };
        break;
      case 'ready': {
        // Reuse the previous derivation when the scene object is identical so a
        // parent re-render that rebuilds the load input does not thrash ids.
        const model =
          previous.status === 'ready' && previous.model.scene === input.scene
            ? previous.model
            : deriveModel(input.scene);
        next = {
          status: 'ready',
          sceneUrl: input.sceneUrl,
          scene: input.scene,
          model,
        };
        break;
      }
      default: {
        const _exhaustive: never = input;
        return _exhaustive;
      }
    }

    load.setState((current) => (isSameLoad(current, next) ? current : next));

    // Reset and re-frame when the drawing has genuinely changed, when geometry
    // first arrives for the current URL, or when the same URL delivers a new
    // scene object — loop ids are positional within a derivation, so a
    // re-derived model must not keep a selection built against the old one.
    const isNewModel =
      next.status === 'ready' &&
      previousModel !== null &&
      next.model !== previousModel;

    if (
      isNewDrawing ||
      (previousModel === null && next.status === 'ready') ||
      isNewModel
    ) {
      // A new drawing starts unrotated with nothing hidden: layer indices are
      // positional, so carrying them across scenes would hide arbitrary
      // layers. Loop ids are positional within a derivation, so a selection
      // made against the old drawing would silently address different
      // geometry.
      layers.setAllVisible(true);
      layers.setHovered(null);
      selection.clear();
      // A drawing-specific choice, not a global preference: a part someone
      // switched to loop-then-edge on should not leave every other drawing
      // opened afterwards starting in edge mode.
      selectionGranularity.set('loop');
      measure.clear();
      marqueeController.abort();
      pointerTracker.reset();
      viewport.reset();
      // A mapping is keyed by entity, and every entity key in it belongs to the
      // drawing it was fetched for. Carrying one across would paint roles onto
      // whichever geometry of the new drawing happened to share a handle, so both
      // the working copy and the saved copy go — the fetch boundary supplies the
      // new drawing's mapping, if it has one.
      //
      // `mode.set` bails when already viewing, so `endSession` is called outright
      // rather than relied on as that call's side effect.
      mode.set('view');
      mapping.endSession();
      mapping.loadSaved(null);
      // Last, so the stack's only entry is the cleared state. Every snapshot on it
      // held loop ids from the previous derivation, which address different
      // geometry now — or none at all.
      history.reset();
      // `mode.set('view')` bails when already viewing, so a brush armed during
      // mapping survives a line-item hop unless the tool is cleared here too.
      if (isBrushTool(tools.getState())) {
        tools.set('select');
      }
    }
  };
};
